import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  takeUntil,
  forkJoin,
  finalize,
  Subject,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  catchError,
} from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside/click-outside.directive';
import {
  ConnectionUser,
  SharedConnectionUsers,
} from '../../../../shared/models/shared-connection-users';
import { PaginatedResponse } from '../../../../shared/interfaces/pagination';
import { AuthService } from '../../../auth/services/auth.service';
import { FileDetails } from '../../models/file-details';
import { SharedConnection, SharedUser } from '../../models/file-share-details';
import { FileUploadService } from '../../services/file-upload.service';

interface ExtendedConnectionUser extends ConnectionUser {
  name: string; // Manually added for UI display
  connection_name: string;
  connection_display_name: string;
}

@Component({
  selector: 'app-document-share',
  standalone: true,
  imports: [FormsModule, PopupComponent, ClickOutsideDirective],
  templateUrl: './document-share.component.html',
  styleUrl: './document-share.component.scss',
})
export class DocumentShareComponent
  extends DestroyBase
  implements OnChanges, OnInit
{
  fileUploadService = inject(FileUploadService);
  authService = inject(AuthService);

  @Input() showSharePopup = false;
  @Input() shareFile!: FileDetails;
  @Output() sharePopupClosed = new EventEmitter<void>();
  @Output() shareCompleted = new EventEmitter<{
    message: string;
    type: 'success' | 'error';
  }>();
  @Output() filesNeedRefresh = new EventEmitter<void>();

  // Share functionality properties
  userSuggestions: ExtendedConnectionUser[] = [];
  showExternalShareConfirmation = false;
  selectedUsersToShare: number[] = [];
  selectedUsersWithNames: {
    displayName: string;
    username: string;
    userId: number;
    connectionName?: string;
  }[] = [];
  selectedThroughConnections = new Set<string>();
  isLoadingUsers = false;
  isUsersDropdownOpen = false;
  searchText = '';
  shareErrors = {
    recipients: '',
  };
  loggedInUsername = this.authService.getLoggedInUserInfo()?.username || '';

  // Pagination properties for user search
  hasMoreUsers = false;
  isLoadingMore = false;
  private userCurrentPage = 1;
  private userPageSize = 10;
  private searchSubject = new Subject<string>();

  // Existing shares properties
  existingSharedConnections: SharedConnection[] = [];
  existingSharedUsers: SharedUser[] = [];
  connectionsToRemove: number[] = [];
  usersToRemove: number[] = [];
  isLoadingExistingShares = false;

  get isShareButtonDisabled(): boolean {
    const hasNewRecipientsToAdd = this.selectedUsersToShare.length > 0;

    const hasExistingToRemove =
      this.connectionsToRemove.length > 0 || this.usersToRemove.length > 0;

    const hasExistingShares =
      this.existingSharedConnections.length > 0 ||
      this.existingSharedUsers.length > 0;

    // Enable if there are new recipients to add, or existing ones to remove, or if there are existing shares
    return !(hasNewRecipientsToAdd || hasExistingToRemove || hasExistingShares);
  }

  ngOnInit(): void {
    this.setupSearchSubscription();
  }

  ngOnChanges(): void {
    if (this.showSharePopup && this.shareFile) {
      this.loadShareData();
    }
  }

  onSharePopupClose(): void {
    this.clearShareFields();
    this.sharePopupClosed.emit();
  }

  setupSearchSubscription(): void {
    this.searchSubject
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          this.isLoadingUsers = true;
          this.userCurrentPage = 1;
          // this.searchText = term; // Removed to avoid interfering with ngModel

          // Check if term looks like an email
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          const isEmail = term && emailRegex.test(term);

          const requests = {
            users: isEmail
              ? of({
                  results: [] as SharedConnectionUsers[],
                  count: 0,
                  next: null,
                  previous: null,
                } as PaginatedResponse<SharedConnectionUsers>)
              : this.fileUploadService
                  .getUsersForSharing(
                    this.shareFile.id,
                    term,
                    this.userCurrentPage,
                    this.userPageSize,
                  )
                  .pipe(
                    catchError(() =>
                      of({
                        results: [] as SharedConnectionUsers[],
                        count: 0,
                        next: null,
                        previous: null,
                      } as PaginatedResponse<SharedConnectionUsers>),
                    ),
                  ),
            emailUsers: isEmail
              ? this.fileUploadService
                  .searchUsersByEmail(this.shareFile.id, term)
                  .pipe(
                    catchError(() =>
                      of({
                        results: [] as SharedConnectionUsers[],
                        count: 0,
                        next: null,
                        previous: null,
                      } as PaginatedResponse<SharedConnectionUsers>),
                    ),
                  )
              : of({
                  results: [] as SharedConnectionUsers[],
                  count: 0,
                  next: null,
                  previous: null,
                } as PaginatedResponse<SharedConnectionUsers>),
          };

          return forkJoin(requests).pipe(
            finalize(() => {
              this.isLoadingUsers = false;
            }),
          );
        }),
      )
      .subscribe({
        next: (response: {
          users: PaginatedResponse<SharedConnectionUsers>;
          emailUsers: PaginatedResponse<SharedConnectionUsers>;
        }) => {
          const { users, emailUsers: emailUsersResponse } = response;
          // Process regular users
          const regularUsers = this.flattenUsers(users.results);

          // Process email users
          const emailUsers = this.flattenUsers(emailUsersResponse.results);

          // Merge lists, prioritizing email matches if any, and removing duplicates
          const mergedUsers = [...emailUsers, ...regularUsers];

          // Filter duplicates based on id
          this.userSuggestions = mergedUsers.filter(
            (user, index, self) =>
              index === self.findIndex((t) => t.id === user.id),
          );

          this.hasMoreUsers =
            this.userCurrentPage * this.userPageSize < (users.count || 0);
        },
        error: (error) => {
          console.error('Error in search subscription:', error);
          this.isLoadingUsers = false;
        },
      });
  }

  loadShareData(): void {
    this.isLoadingExistingShares = true;
    this.isLoadingUsers = true;
    this.userCurrentPage = 1;

    // Load existing shares and available users/connections in parallel using forkJoin
    forkJoin({
      existingShares: this.fileUploadService.getFileSharedDetails(
        this.shareFile.id,
      ),
      availableUsers: this.fileUploadService.getUsersForSharing(
        this.shareFile.id,
        this.searchText,
        this.userCurrentPage,
        this.userPageSize,
      ),
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingExistingShares = false;
          this.isLoadingUsers = false;
        }),
      )
      .subscribe({
        next: ({ existingShares, availableUsers }) => {
          const { connections, users } = existingShares;
          // Handle existing shares
          this.existingSharedConnections = connections;
          this.existingSharedUsers = users;

          // Handle available users - flatten the list
          this.userSuggestions = this.flattenUsers(availableUsers.results);

          this.hasMoreUsers =
            this.userCurrentPage * this.userPageSize <
            (availableUsers.count || 0);
        },
        error: (error) => {
          console.error('Error loading share data:', error);
        },
      });
  }

  flattenUsers(results: SharedConnectionUsers[]): ExtendedConnectionUser[] {
    if (!results) return [];
    return results.flatMap((conn) =>
      conn.users
        .filter(
          (user) => user.username !== this.shareFile.uploaded_user.username,
        )
        .map((user) => ({
          ...user,
          name: `${user.first_name} ${user.last_name}`,
          connection_name: conn.connection_name,
          connection_display_name: conn.display_name || conn.connection_name,
        })),
    );
  }

  onConfirmShare(): void {
    const currentConnection =
      this.authService.getLoggedInUserInfo()?.connection_display_name;
    const hasExternalUsers = this.selectedUsersWithNames.some(
      (user) =>
        user.connectionName && user.connectionName !== currentConnection,
    );

    if (hasExternalUsers) {
      this.showExternalShareConfirmation = true;
      return;
    }

    this.confirmShareOperation();
  }

  cancelShareConfirmation(): void {
    this.showExternalShareConfirmation = false;
  }

  confirmShareOperation(): void {
    this.showExternalShareConfirmation = false;
    this.shareErrors = { recipients: '' };

    // Prepare connections and users to share (new ones)
    const connectionsToShare: number[] = [];

    // Filter out user IDs that belong to selected connections for sharing
    const usersToShare = this.selectedUsersToShare;

    // Prepare connections and users to unshare (marked for removal)
    const connectionsToUnshare = this.connectionsToRemove;
    const usersToUnshare = this.usersToRemove;

    this.fileUploadService
      .shareFile(
        this.shareFile.id,
        connectionsToShare,
        usersToShare,
        connectionsToUnshare,
        usersToUnshare,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.clearShareFields();
          this.shareCompleted.emit({
            message: 'File sharing updated successfully!',
            type: 'success',
          });
          this.filesNeedRefresh.emit();
          this.sharePopupClosed.emit();
        },
        error: (error) => {
          console.error('Error updating file sharing:', error);
          let errorMessage = 'Failed to update file sharing. Please try again.';
          if (error?.error?.user_ids && error.error.user_ids.length > 0) {
            errorMessage = error.error.user_ids[0];
          }
          this.shareCompleted.emit({
            message: errorMessage,
            type: 'error',
          });
        },
      });
  }

  onSearchUser(value: string): void {
    this.searchSubject.next(value.trim());
  }

  onCloseDropdown(): void {
    this.isUsersDropdownOpen = false;
    this.searchText = '';
    this.searchSubject.next('');
  }

  onInputFocus(): void {
    this.isUsersDropdownOpen = true;
    // If no data has been loaded yet, load it
    if (this.userSuggestions.length === 0 && !this.isLoadingUsers) {
      this.searchSubject.next(this.searchText);
    }
  }

  onUsersSelection(
    event: Event,
    {
      username,
      name,
      userId,
      connectionName,
    }: {
      username: string;
      name: string;
      userId: number;
      connectionName?: string;
    },
  ): void {
    if (this.selectedUsersToShare.includes(userId)) {
      this.selectedUsersToShare = this.selectedUsersToShare.filter(
        (u) => u !== userId,
      );
      this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
        (u) => u.username !== username,
      );
    } else {
      this.selectedUsersToShare.push(userId);
      this.selectedUsersWithNames.push({
        displayName: name,
        username,
        userId,
        connectionName,
      });
    }
  }

  getSelectedUsers(): string[] {
    return this.selectedUsersWithNames
      .filter((user) => !this.selectedThroughConnections.has(user.username))
      .map((user) =>
        user.connectionName
          ? `${user.displayName} (${user.connectionName})`
          : user.displayName,
      );
  }

  removeUser(formattedName: string): void {
    const user = this.selectedUsersWithNames.find((u) => {
      const nameToCheck = u.connectionName
        ? `${u.displayName} (${u.connectionName})`
        : u.displayName;
      return nameToCheck === formattedName;
    });

    if (user) {
      this.selectedUsersToShare = this.selectedUsersToShare.filter(
        (userId) => userId !== user.userId,
      );
      this.selectedUsersWithNames = this.selectedUsersWithNames.filter(
        (u) => u.userId !== user.userId,
      );
    }
  }

  removeExistingConnection(connection: SharedConnection): void {
    if (!this.connectionsToRemove.includes(connection.id)) {
      this.connectionsToRemove.push(connection.id);
    }
  }

  removeExistingUser(user: SharedUser): void {
    if (!this.usersToRemove.includes(user.id)) {
      this.usersToRemove.push(user.id);
    }
  }

  restoreExistingConnection(connection: SharedConnection): void {
    this.connectionsToRemove = this.connectionsToRemove.filter(
      (id) => id !== connection.id,
    );
  }

  restoreExistingUser(user: SharedUser): void {
    this.usersToRemove = this.usersToRemove.filter((id) => id !== user.id);
  }

  isConnectionMarkedForRemoval(connectionId: number): boolean {
    return this.connectionsToRemove.includes(connectionId);
  }

  isUserMarkedForRemoval(userId: number): boolean {
    return this.usersToRemove.includes(userId);
  }

  clearShareFields(): void {
    this.selectedUsersToShare = [];
    this.selectedUsersWithNames = [];
    this.selectedThroughConnections.clear();
    this.searchText = '';
    this.shareErrors = { recipients: '' };
    this.isUsersDropdownOpen = false;
    this.existingSharedConnections = [];
    this.existingSharedUsers = [];
    this.connectionsToRemove = [];
    this.usersToRemove = [];
    this.hasMoreUsers = false;
    this.showExternalShareConfirmation = false;
    this.userCurrentPage = 1;
  }

  loadMoreUsers(): void {
    if (this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.userCurrentPage++;

    this.fileUploadService
      .getUsersForSharing(
        this.shareFile.id,
        this.searchText,
        this.userCurrentPage,
        this.userPageSize,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Filter and append new results
          const newSuggestions = this.flattenUsers(response.results);

          // Append only if not already in list (though pagination usually avoids overlap, but good to be safe)
          const uniqueNewSuggestions = newSuggestions.filter(
            (newUser) =>
              !this.userSuggestions.some(
                (existingUser) => existingUser.id === newUser.id,
              ),
          );

          this.userSuggestions = [
            ...this.userSuggestions,
            ...uniqueNewSuggestions,
          ];

          this.hasMoreUsers =
            this.userCurrentPage * this.userPageSize < (response.count || 0);
          this.isLoadingMore = false;
        },
        error: () => {
          this.isLoadingMore = false;
        },
      });
  }
}
